import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Plus, X, ClipboardList, Trash2, Edit2, Search } from 'lucide-react';

function formatTelefone(tel) {
  if (!tel) return '—';
  const d = tel.replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0,2)}) ${d[2]} ${d.slice(3,7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  return tel;
}
function formatData(val) {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}
function formatSexo(s) {
  if (s === 'M') return 'Masculino';
  if (s === 'F') return 'Feminino';
  return s || '—';
}

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const map = {
    aguardando_avaliacao: { label: 'Aguardando Avaliação', cls: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    em_tratamento: { label: 'Em Tratamento', cls: 'bg-green-100 text-green-800 border-green-200' },
    concluido: { label: 'Concluído', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  };
  const item = map[status];
  if (!item) return null;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${item.cls}`}>{item.label}</span>;
};

const emptyFisio = { tipoPaciente: 'fisioterapia', nome: '', email: '', telefone: '', dateOfBirth: '', sex: '', profissionalResponsavel: '', numberOfSessions: '', tipoCobranca: 'sessao', valorSessao: '', valorPacote: '', percentualRepasse: '' };
const emptyPilates = { tipoPaciente: 'pilates', nome: '', email: '', telefone: '', dateOfBirth: '', sex: '', mensalidade: '', diaVencimento: '', diagnosisTitle: '' };

export default function ClinicaPacientesPage() {
  const [aba, setAba] = useState('fisioterapia');
  const [pacientesFisio, setPacientesFisio] = useState([]);
  const [pacientesPilates, setPacientesPilates] = useState([]);
  const [equipe, setEquipe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyFisio);
  const [saving, setSaving] = useState(false);
  const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
  const [evolucoes, setEvolucoes] = useState([]);
  const [loadingEvolucoes, setLoadingEvolucoes] = useState(false);
  const [abaModal, setAbaModal] = useState('dados');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ profissionalResponsavel: '', percentualRepasse: '', mensalidade: '', diaVencimento: '', numberOfSessions: '', valorSessao: '', valorPacote: '', tipoCobranca: 'sessao', diagnosisTitle: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [busca, setBusca] = useState('');

  const fetchPacientes = () => {
    Promise.all([
      api.get('/clinica/meus-pacientes?tipo=fisioterapia'),
      api.get('/clinica/meus-pacientes?tipo=pilates'),
      api.get('/clinica/dashboard'),
    ]).then(([f, p, d]) => {
      setPacientesFisio(f.data.data);
      setPacientesPilates(p.data.data);
      setEquipe(d.data.data.equipe || []);
    }).catch(() => toast.error('Erro ao carregar dados'))
    .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPacientes(); }, []);

  const abrirModal = () => {
    setForm(aba === 'fisioterapia' ? emptyFisio : emptyPilates);
    setShowModal(true);
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    if (!form.nome) return toast.error('Nome é obrigatório');
    if (aba === 'fisioterapia' && !form.profissionalResponsavel) return toast.error('Fisioterapeuta responsável é obrigatório');
    setSaving(true);
    try {
      const { data } = await api.post('/clinica/meus-pacientes', { ...form, tipoPaciente: aba });
      if (aba === 'fisioterapia') setPacientesFisio(p => [...p, data.data]);
      else setPacientesPilates(p => [...p, data.data]);
      setShowModal(false);
      toast.success('Paciente cadastrado!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao cadastrar');
    } finally { setSaving(false); }
  };

  const handleExcluir = async (p, e) => {
    e.stopPropagation();
    if (!window.confirm(`Excluir "${p.nome}" e todos os dados vinculados?`)) return;
    try {
      await api.delete(`/clinica/meus-pacientes/${p._id}`);
      if (aba === 'fisioterapia') setPacientesFisio(list => list.filter(x => x._id !== p._id));
      else setPacientesPilates(list => list.filter(x => x._id !== p._id));
      toast.success('Paciente excluído.');
    } catch { toast.error('Erro ao excluir paciente'); }
  };

  const abrirEditar = (p, e) => {
    e.stopPropagation();
    setPacienteSelecionado(p);
    setEditForm({
      profissionalResponsavel: p.profissionalResponsavel?._id || p.profissionalResponsavel || '',
      percentualRepasse: p.percentualRepasse || '',
      mensalidade: p.mensalidade || '',
      diaVencimento: p.diaVencimento || '',
      numberOfSessions: p.numberOfSessions || '',
      valorSessao: p.valorSessao || '',
      valorPacote: p.valorPacote || '',
      tipoCobranca: p.tipoCobranca || 'sessao',
      diagnosisTitle: p.diagnosisTitle || '',
    });
    setShowEditModal(true);
  };

  const handleSalvarEdicao = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      const paciente = pacienteSelecionado;
      const fisioAtual = paciente.profissionalResponsavel?._id || paciente.profissionalResponsavel || '';
      if (editForm.profissionalResponsavel && editForm.profissionalResponsavel !== fisioAtual) {
        await api.patch(`/clinica/meus-pacientes/${paciente._id}/trocar-fisio`, {
          novoFisioId: editForm.profissionalResponsavel,
        });
      }
      const { data } = await api.put(`/clinica/meus-pacientes/${paciente._id}`, {
        percentualRepasse: editForm.percentualRepasse || null,
        mensalidade: editForm.mensalidade || null,
        diaVencimento: editForm.diaVencimento || null,
        numberOfSessions: editForm.numberOfSessions || null,
        valorSessao: editForm.valorSessao || null,
        valorPacote: editForm.valorPacote || null,
        tipoCobranca: editForm.tipoCobranca,
        profissionalResponsavel: editForm.profissionalResponsavel || null,
        diagnosisTitle: editForm.diagnosisTitle || null,
      });
      if (aba === 'fisioterapia') setPacientesFisio(list => list.map(x => x._id === paciente._id ? data.data : x));
      else setPacientesPilates(list => list.map(x => x._id === paciente._id ? data.data : x));
      setShowEditModal(false);
      setPacienteSelecionado(null);
      toast.success('Paciente atualizado!');
    } catch { toast.error('Erro ao atualizar'); }
    finally { setSavingEdit(false); }
  };

  const abrirModalPaciente = (p) => {
    setPacienteSelecionado(p);
    setAbaModal('dados');
    setEvolucoes([]);
  };

  const carregarEvolucoes = async (patientRef) => {
    if (!patientRef) return toast.error('Paciente sem vínculo com fisioterapeuta.');
    setAbaModal('evolucoes');
    if (evolucoes.length > 0) return;
    setLoadingEvolucoes(true);
    try {
      const id = typeof patientRef === 'object' ? patientRef._id : patientRef;
      const { data } = await api.get(`/clinica/evolucoes/${id}`);
      setEvolucoes(data.data);
    } catch { toast.error('Erro ao carregar evoluções'); }
    finally { setLoadingEvolucoes(false); }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Carregando...</div>;

  const pacientesAtivos = (aba === 'fisioterapia' ? pacientesFisio : pacientesPilates)
    .filter(p => busca.trim() === '' || p.nome.toLowerCase().includes(busca.toLowerCase()));

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Pacientes</h1>
        <button onClick={abrirModal} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition-colors">
          <Plus className="w-4 h-4" /> Novo Paciente
        </button>
      </div>

      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {[{ key: 'fisioterapia', label: 'Fisioterapia' }, { key: 'pilates', label: 'Pilates' }].map(({ key, label }) => (
          <button key={key} onClick={() => { setAba(key); setBusca(''); }}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${aba === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder="Buscar paciente pelo nome..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
        />
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {pacientesAtivos.length === 0 ? (
          <p className="text-sm text-gray-400 p-6 text-center">
            {busca ? `Nenhum paciente encontrado para "${busca}".` : 'Nenhum paciente cadastrado.'}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-left border-b border-gray-100">
              <tr>
                <th className="px-5 py-3 text-xs font-semibold uppercase">Nome</th>
                {aba === 'fisioterapia' && <>
                  <th className="px-5 py-3 text-xs font-semibold uppercase hidden sm:table-cell">Status</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase hidden sm:table-cell">Diagnóstico</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase hidden md:table-cell">Fisioterapeuta</th>
                </>}
                {aba === 'pilates' && <>
                  <th className="px-5 py-3 text-xs font-semibold uppercase hidden sm:table-cell">Email</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase">Mensalidade</th>
                </>}
                <th className="px-5 py-3 text-xs font-semibold uppercase text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {pacientesAtivos.map(p => (
                <tr key={p._id}
                  className={`transition-colors cursor-pointer ${aba === 'pilates' && p.diagnosisTitle ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'}`}
                  onClick={() => abrirModalPaciente(p)}>
                  <td className="px-5 py-4">
                    <p className="font-medium text-gray-800">{p.nome}</p>
                    {aba === 'pilates' && p.diagnosisTitle && (
                      <p className="text-xs text-amber-600 font-medium mt-0.5">⚠ {p.diagnosisTitle}</p>
                    )}
                  </td>
                  {aba === 'fisioterapia' && <>
                    <td className="px-5 py-4 hidden sm:table-cell"><StatusBadge status={p.patientRef?.status} /></td>
                    <td className="px-5 py-4 text-gray-500 hidden sm:table-cell">{p.patientRef?.diagnosisTitle || p.diagnosisTitle || '—'}</td>
                    <td className="px-5 py-4 text-gray-500 hidden md:table-cell">{p.profissionalResponsavel?.nome || '—'}</td>
                  </>}
                  {aba === 'pilates' && <>
                    <td className="px-5 py-4 text-gray-500 hidden sm:table-cell">{p.email || '—'}</td>
                    <td className="px-5 py-4 text-gray-700">R$ {p.mensalidade?.toFixed(2) || '0.00'}</td>
                  </>}
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-2" onClick={e => e.stopPropagation()}>
                      <button onClick={(e) => abrirEditar(p, e)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Editar">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={(e) => handleExcluir(p, e)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Excluir">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal detalhe */}
      {pacienteSelecionado && !showEditModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md relative flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{pacienteSelecionado.nome}</h3>
                {aba === 'fisioterapia' && <StatusBadge status={pacienteSelecionado.patientRef?.status} />}
              </div>
              <button onClick={() => setPacienteSelecionado(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            {aba === 'fisioterapia' && (
              <div className="flex border-b border-gray-100">
                <button onClick={() => setAbaModal('dados')} className={`flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors ${abaModal === 'dados' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}>Dados</button>
                <button onClick={() => carregarEvolucoes(pacienteSelecionado.patientRef)} className={`flex-1 py-2.5 text-sm font-medium border-b-2 flex items-center justify-center gap-1.5 transition-colors ${abaModal === 'evolucoes' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}>
                  <ClipboardList className="w-4 h-4" /> Evoluções
                </button>
              </div>
            )}
            <div className="p-6 overflow-y-auto flex-1">
              {abaModal === 'dados' && (
                <dl className="space-y-3 text-sm">
                  {[
                    aba === 'fisioterapia' && ['Fisioterapeuta', pacienteSelecionado.profissionalResponsavel?.nome],
                    ['Diagnóstico / Patologia', pacienteSelecionado.patientRef?.diagnosisTitle || pacienteSelecionado.diagnosisTitle],
                    ['Sexo', formatSexo(pacienteSelecionado.sex)],
                    ['Nascimento', formatData(pacienteSelecionado.dateOfBirth)],
                    ['Telefone', formatTelefone(pacienteSelecionado.telefone)],
                    ['Email', pacienteSelecionado.email],
                    aba === 'fisioterapia' && ['Sessões', pacienteSelecionado.numberOfSessions],
                    aba === 'fisioterapia' && ['Cobrança', pacienteSelecionado.tipoCobranca === 'pacote' ? 'Pacote' : 'Por Sessão'],
                    aba === 'fisioterapia' && pacienteSelecionado.percentualRepasse && ['Repasse ao Fisio', `${pacienteSelecionado.percentualRepasse}%`],
                    aba === 'pilates' && ['Mensalidade', `R$ ${pacienteSelecionado.mensalidade?.toFixed(2) || '0.00'}`],
                    aba === 'pilates' && pacienteSelecionado.diaVencimento && ['Vencimento', `Dia ${pacienteSelecionado.diaVencimento}`],
                  ].filter(Boolean).map(([label, value]) => (
                    <div key={label} className="flex gap-2">
                      <dt className="text-gray-500 w-36 shrink-0">{label}:</dt>
                      <dd className={`text-gray-800 ${label === 'Diagnóstico / Patologia' && value ? 'text-amber-600 font-medium' : ''}`}>{value || '—'}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {abaModal === 'evolucoes' && (
                loadingEvolucoes ? <p className="text-sm text-gray-400 text-center py-8">Carregando...</p>
                : !evolucoes.length ? <p className="text-sm text-gray-400 text-center py-8">Nenhuma evolução registrada.</p>
                : <ul className="space-y-3">
                    {evolucoes.map(ev => (
                      <li key={ev._id} className="border border-gray-100 rounded-xl p-4">
                        <p className="text-xs text-gray-400 mb-1">{ev.session?.sessionDate ? formatData(ev.session.sessionDate) : '—'}</p>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{ev.description}</p>
                      </li>
                    ))}
                  </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal editar */}
      {showEditModal && pacienteSelecionado && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowEditModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Editar Paciente</h2>
            <p className="text-sm text-gray-400 mb-5">{pacienteSelecionado.nome}</p>
            <form onSubmit={handleSalvarEdicao} className="space-y-4">
              {aba === 'fisioterapia' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fisioterapeuta Responsável</label>
                    <select value={editForm.profissionalResponsavel} onChange={e => setEditForm(f => ({ ...f, profissionalResponsavel: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none">
                      <option value="">Selecione</option>
                      {equipe.map(f => <option key={f._id} value={f._id}>{f.nome}</option>)}
                    </select>
                    <p className="text-xs text-gray-400 mt-1">Trocar o fisioterapeuta transfere o paciente para o novo profissional.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nº de Sessões</label>
                    <input type="number" value={editForm.numberOfSessions} onChange={e => setEditForm(f => ({ ...f, numberOfSessions: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Cobrança</label>
                    <select value={editForm.tipoCobranca} onChange={e => setEditForm(f => ({ ...f, tipoCobranca: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none">
                      <option value="sessao">Por Sessão</option>
                      <option value="pacote">Pacote</option>
                    </select>
                  </div>
                  {editForm.tipoCobranca === 'sessao' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Valor por Sessão (R$)</label>
                      <input type="number" value={editForm.valorSessao} onChange={e => setEditForm(f => ({ ...f, valorSessao: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                    </div>
                  )}
                  {editForm.tipoCobranca === 'pacote' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Valor do Pacote (R$)</label>
                      <input type="number" value={editForm.valorPacote} onChange={e => setEditForm(f => ({ ...f, valorPacote: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Repasse ao Fisioterapeuta (%)</label>
                    <input type="number" min="0" max="100" value={editForm.percentualRepasse}
                      onChange={e => setEditForm(f => ({ ...f, percentualRepasse: e.target.value }))}
                      placeholder="Ex: 30"
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                    {editForm.percentualRepasse && editForm.valorSessao && (
                      <p className="text-xs text-gray-500 mt-1">
                        Fisio recebe: R$ {(Number(editForm.valorSessao) * Number(editForm.percentualRepasse) / 100).toFixed(2)}/sessão &nbsp;·&nbsp; Clínica: R$ {(Number(editForm.valorSessao) * (1 - Number(editForm.percentualRepasse) / 100)).toFixed(2)}/sessão
                      </p>
                    )}
                  </div>
                </>
              )}
              {aba === 'pilates' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Patologia / Condição de Saúde</label>
                    <input type="text" placeholder="Ex: Hérnia de disco, Artrose..."
                      value={editForm.diagnosisTitle} onChange={e => setEditForm(f => ({ ...f, diagnosisTitle: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                    <p className="text-xs text-gray-400 mt-1">Visível para professores da turma.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mensalidade (R$)</label>
                    <input type="number" value={editForm.mensalidade} onChange={e => setEditForm(f => ({ ...f, mensalidade: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dia de Vencimento</label>
                    <input type="number" min="1" max="31" placeholder="Ex: 10"
                      value={editForm.diaVencimento} onChange={e => setEditForm(f => ({ ...f, diaVencimento: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                  </div>
                </>
              )}
              <button type="submit" disabled={savingEdit}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-xl text-sm font-medium transition-colors">
                {savingEdit ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Modal novo paciente */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Novo Paciente</h2>
            <p className="text-sm text-gray-400 mb-5">{aba === 'fisioterapia' ? 'Paciente de Fisioterapia' : 'Paciente de Pilates'}</p>
            <form onSubmit={handleSalvar} className="space-y-4">
              {[['nome','Nome *','text'],['email','Email','email'],['telefone','Telefone','text'],['dateOfBirth','Data de Nascimento','date']].map(([name,label,type]) => (
                <div key={name}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input type={type} value={form[name]} onChange={e => setForm(f => ({...f,[name]:e.target.value}))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sexo</label>
                <select value={form.sex} onChange={e => setForm(f => ({...f,sex:e.target.value}))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none">
                  <option value="">Selecione</option>
                  <option value="M">Masculino</option>
                  <option value="F">Feminino</option>
                  <option value="Outro">Outro</option>
                </select>
              </div>
              {aba === 'fisioterapia' && <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fisioterapeuta Responsável *</label>
                  <select value={form.profissionalResponsavel} onChange={e => setForm(f => ({...f,profissionalResponsavel:e.target.value}))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none">
                    <option value="">Selecione</option>
                    {equipe.map(f => <option key={f._id} value={f._id}>{f.nome}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nº de Sessões</label>
                  <input type="number" value={form.numberOfSessions} onChange={e => setForm(f => ({...f,numberOfSessions:e.target.value}))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Cobrança</label>
                  <select value={form.tipoCobranca} onChange={e => setForm(f => ({...f,tipoCobranca:e.target.value}))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none">
                    <option value="sessao">Por Sessão</option>
                    <option value="pacote">Pacote</option>
                  </select>
                </div>
                {form.tipoCobranca === 'sessao' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valor por Sessão (R$)</label>
                    <input type="number" value={form.valorSessao} onChange={e => setForm(f => ({...f,valorSessao:e.target.value}))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                  </div>
                )}
                {form.tipoCobranca === 'pacote' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valor do Pacote (R$)</label>
                    <input type="number" value={form.valorPacote} onChange={e => setForm(f => ({...f,valorPacote:e.target.value}))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Repasse ao Fisioterapeuta (%)</label>
                  <input type="number" min="0" max="100" value={form.percentualRepasse}
                    onChange={e => setForm(f => ({...f,percentualRepasse:e.target.value}))}
                    placeholder="Ex: 30"
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                  {form.percentualRepasse && form.valorSessao && (
                    <p className="text-xs text-gray-500 mt-1">
                      Fisio recebe: R$ {(Number(form.valorSessao) * Number(form.percentualRepasse) / 100).toFixed(2)}/sessão &nbsp;·&nbsp; Clínica retém: R$ {(Number(form.valorSessao) * (1 - Number(form.percentualRepasse) / 100)).toFixed(2)}/sessão
                    </p>
                  )}
                </div>
              </>}
              {aba === 'pilates' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Patologia / Condição de Saúde</label>
                    <input type="text" placeholder="Ex: Hérnia de disco, Artrose, Osteoporose..."
                      value={form.diagnosisTitle} onChange={e => setForm(f => ({...f, diagnosisTitle: e.target.value}))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                    <p className="text-xs text-gray-400 mt-1">Visível para professores da turma ao ministrar a aula.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mensalidade (R$)</label>
                    <input type="number" value={form.mensalidade} onChange={e => setForm(f => ({...f,mensalidade:e.target.value}))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dia de Vencimento</label>
                    <input type="number" min="1" max="31" placeholder="Ex: 10"
                      value={form.diaVencimento} onChange={e => setForm(f => ({...f,diaVencimento:e.target.value}))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 outline-none" />
                  </div>
                </>
              )}
              <button type="submit" disabled={saving}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-xl text-sm font-medium transition-colors">
                {saving ? 'Salvando...' : 'Salvar Paciente'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}